import { useState } from 'react';
import { Modal, Button, TextInput, Group, Text, Stack } from '@mantine/core';
import { IconEdit, IconTrash } from '@tabler/icons-react';
import styles from './ConversationOptionsModal.module.css';

export interface ConversationOptionsModalProps {
  conversationId: string;
  opened: boolean;
  onClose: () => void;
  onRename: (conversationId: string) => void;
  onDelete: (conversationId: string) => void;
}

export function ConversationOptionsModal({
  conversationId,
  opened,
  onClose,
  onRename,
  onDelete
}: ConversationOptionsModalProps) {
  const [view, setView] = useState<'menu' | 'rename' | 'delete'>('menu');
  const [newTitle, setNewTitle] = useState('');
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setView('menu');
    setNewTitle('');
    setLoading(false);
    onClose();
  };

  const handleRename = async () => {
    if (!newTitle.trim()) return;
    
    setLoading(true);
    try {
      // TODO: Implement actual rename API call
      await new Promise(resolve => setTimeout(resolve, 500)); // Mock API call
      onRename(conversationId);
      handleClose();
    } catch (error) {
      console.error('Failed to rename conversation:', error);
    } finally {
      setLoading(false);
    }
  };


  const handleDelete = async () => {
    setLoading(true);
    try {
      // TODO: Implement actual delete API call
      await new Promise(resolve => setTimeout(resolve, 500)); // Mock API call
      onDelete(conversationId);
      handleClose();
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderMenuView = () => (
    <Stack gap="sm">
      <Text size="sm" c="dimmed" mb="sm">
        Choose an action for this conversation
      </Text>
      
      <Button
        variant="subtle"
        fullWidth
        justify="flex-start"
        leftSection={<IconEdit size={16} />}
        onClick={() => setView('rename')}
        className={styles.actionButton}
      >
        Rename conversation
      </Button>
      
      <Button
        variant="subtle"
        fullWidth
        justify="flex-start"
        leftSection={<IconTrash size={16} />}
        onClick={() => setView('delete')}
        className={styles.actionButton}
        c="red"
      >
        Delete conversation
      </Button>
    </Stack>
  );

  const renderRenameView = () => (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        Enter a new name for this conversation
      </Text>
      
      <TextInput
        placeholder="Conversation title"
        value={newTitle}
        onChange={(event) => setNewTitle(event.currentTarget.value)}
        data-autofocus
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            handleRename();
          }
        }}
      />
      
      <Group justify="flex-end" gap="sm">
        <Button
          variant="subtle"
          onClick={() => setView('menu')}
          disabled={loading}
          className={styles.cancelButton}
        >
          Cancel
        </Button>
        <Button
          onClick={handleRename}
          loading={loading}
          disabled={!newTitle.trim()}
        >
          Rename
        </Button>
      </Group>
    </Stack>
  );


  const renderDeleteView = () => (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        Are you sure you want to delete this conversation? This action cannot be undone.
      </Text>
      
      <Group justify="flex-end" gap="sm">
        <Button
          variant="subtle"
          onClick={() => setView('menu')}
          disabled={loading}
          className={styles.cancelButton}
        >
          Cancel
        </Button>
        <Button
          color="red"
          onClick={handleDelete}
          loading={loading}
        >
          Delete
        </Button>
      </Group>
    </Stack>
  );

  const getModalTitle = () => {
    switch (view) {
      case 'rename':
        return 'Rename Conversation';
      case 'delete':
        return 'Delete Conversation';
      default:
        return 'Conversation Options';
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={getModalTitle()}
      size="sm"
      centered
      classNames={{
        root: styles.modal,
        header: styles.modalHeader,
        title: styles.modalTitle,
        body: styles.modalBody,
      }}
    >
      {view === 'menu' && renderMenuView()}
      {view === 'rename' && renderRenameView()}
      {view === 'delete' && renderDeleteView()}
    </Modal>
  );
}
